const resolveClient = async (clientSource) =>
  typeof clientSource === "function" ? clientSource() : clientSource;

const firstRow = (data) => (Array.isArray(data) ? data[0] : data);

// Answer keys live in `lesson_quiz_answer_keys`, not in `lessons.quiz_data`:
// enrolled learners can read their lesson rows, and a database trigger strips
// these fields from quiz_data on every write.
export const QUIZ_ANSWER_KEY_FIELDS = [
  "correctAnswers",
  "correct_answer",
  "sampleAnswer",
  "sample_answer",
  "explanation",
];

export const splitQuizAnswerKeys = (quizData) => {
  const questions = Array.isArray(quizData?.questions)
    ? quizData.questions
    : null;
  if (!questions) return { quizData, answerKeys: {} };

  const answerKeys = {};
  const strippedQuestions = questions.map((question) => {
    if (!question || typeof question !== "object") return question;

    const stripped = { ...question };
    const answerKey = {};
    QUIZ_ANSWER_KEY_FIELDS.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(stripped, field)) return;
      answerKey[field] = stripped[field];
      delete stripped[field];
    });
    if (question.id && Object.keys(answerKey).length > 0) {
      answerKeys[question.id] = answerKey;
    }
    return stripped;
  });

  return {
    quizData: { ...quizData, questions: strippedQuestions },
    answerKeys,
  };
};

export const mergeQuizAnswerKeys = (quizData, answerKeys) => {
  const questions = Array.isArray(quizData?.questions)
    ? quizData.questions
    : null;
  if (!questions || !answerKeys || Object.keys(answerKeys).length === 0) {
    return quizData;
  }

  return {
    ...quizData,
    questions: questions.map((question) => {
      const answerKey = question?.id ? answerKeys[question.id] : null;
      if (!answerKey || typeof answerKey !== "object") return question;
      return { ...question, ...answerKey };
    }),
  };
};

export const createQuizAnswerKeyService = (clientSource) => ({
  async load(lessonIds) {
    const ids = (lessonIds || []).filter(Boolean);
    if (ids.length === 0) return {};

    const client = await resolveClient(clientSource);
    const { data, error } = await client
      .from("lesson_quiz_answer_keys")
      .select("lesson_id, answer_keys")
      .in("lesson_id", ids);
    if (error) throw error;

    return (data || []).reduce((keysByLesson, row) => {
      keysByLesson[row.lesson_id] = row.answer_keys || {};
      return keysByLesson;
    }, {});
  },

  async save(lessonId, answerKeys) {
    const client = await resolveClient(clientSource);
    const { error } = await client.from("lesson_quiz_answer_keys").upsert(
      {
        lesson_id: lessonId,
        answer_keys: answerKeys || {},
        updated_at: new Date().toISOString(),
      },
      { onConflict: "lesson_id" },
    );
    if (error) throw error;
  },
});

export const createLessonMutationService = (clientSource, cleanupStorage) => ({
  async delete(lessonId) {
    const client = await resolveClient(clientSource);
    const { data, error } = await client.rpc("delete_course_lesson", {
      p_lesson_id: lessonId,
    });
    if (error) throw error;

    const deletedLesson = firstRow(data);
    if (!deletedLesson?.id) {
      throw new Error("Lesson was not found or could not be deleted");
    }

    const storageUrls = deletedLesson.storage_urls || [];
    const warnings = [];
    if (storageUrls.length > 0 && cleanupStorage) {
      try {
        await cleanupStorage(storageUrls);
      } catch (cleanupError) {
        warnings.push(`Storage cleanup failed: ${cleanupError.message}`);
      }
    }

    return {
      success: true,
      deletedLesson,
      ...(warnings.length > 0 ? { warnings } : {}),
    };
  },
});
